import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ReactLenis, useLenis } from 'lenis/react';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { setLenisInstance } from '../../util/smoothScroll';

import 'lenis/dist/lenis.css';

/** Routes where Lenis smooth-scrolling is active. */
const LENIS_ROUTES = ['/'];

const isTestEnv = process.env.NODE_ENV === 'test';

const getAnchorOffset = () => {
  if (typeof window === 'undefined') {
    return -100;
  }
  const styles = getComputedStyle(document.documentElement);
  const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
  const raw = isDesktop
    ? styles.getPropertyValue('--topbarHeightDesktop')
    : styles.getPropertyValue('--topbarHeight');
  const px = parseInt(raw, 10);
  return Number.isFinite(px) ? -px : -100;
};

// Soft, slow interpolation — no bounce. Duration is unused while lerp is set.
const LENIS_OPTIONS = {
  autoRaf: true,
  autoToggle: true,
  anchors: { offset: typeof window === 'undefined' ? -100 : getAnchorOffset() },
  allowNestedScroll: true,
  stopInertiaOnNavigate: true,
  respectReducedMotion: true,
  lerp: 0.046,
  wheelMultiplier: 0.6,
  syncTouch: false,
};

/**
 * Stops Lenis while a modal (or any scrollingDisabled request) is open, and
 * keeps a module-level handle so route changes can scrollTo immediately.
 */
const LenisLifecycle = () => {
  const scrollingDisabled = useSelector(isScrollingDisabled);
  const lenis = useLenis();

  useEffect(() => {
    setLenisInstance(lenis);
    return () => {
      setLenisInstance(null);
    };
  }, [lenis]);

  useEffect(() => {
    if (!lenis) {
      return;
    }
    if (scrollingDisabled) {
      lenis.stop();
    } else {
      lenis.start();
    }
  }, [lenis, scrollingDisabled]);

  return null;
};

/**
 * Lenis smooth scrolling — active only on routes listed in `LENIS_ROUTES`
 * (currently just the landing page `/`).  On all other routes the component
 * is a transparent pass-through so native browser scroll is restored.
 *
 * Must be rendered inside `<BrowserRouter>` so `useLocation` is available.
 *
 * @component
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 */
const SmoothScroll = props => {
  const { children } = props;
  const { pathname } = useLocation();

  const isLenisRoute = LENIS_ROUTES.includes(pathname);

  if (isTestEnv || !isLenisRoute) {
    return children;
  }

  return (
    <ReactLenis root options={LENIS_OPTIONS}>
      <LenisLifecycle />
      {children}
    </ReactLenis>
  );
};

export default SmoothScroll;
